import {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react'

import { NotificationType } from 'src/graphql/types/notifications'

interface NotificationFilterContextType {
  selectedFilter: NotificationType | null
  setSelectedFilter: (filter: NotificationType | null) => void
  totalCount: number | undefined
  setTotalCount: (count: number | undefined) => void
  selectedIds: Set<string>
  toggleSelectedId: (id: string) => void
  selectAllIds: (ids: string[]) => void
  clearSelection: () => void
  /** Closes the notifications drawer. No-op when rendered outside it. */
  closeDrawer: () => void
}

const NotificationFilterContext = createContext<
  NotificationFilterContextType | undefined
>(undefined)

export const NotificationFilterProvider = ({
  children,
  onClose,
}: {
  children: ReactNode
  onClose?: () => void
}) => {
  const [selectedFilter, setSelectedFilter] = useState<NotificationType | null>(
    null
  )
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const toggleSelectedId = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAllIds = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        ids.forEach((id) => next.delete(id))
        return next
      }
      return new Set([...prev, ...ids])
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const closeDrawer = useCallback(() => {
    onClose?.()
  }, [onClose])

  return (
    <NotificationFilterContext.Provider
      value={{
        selectedFilter,
        setSelectedFilter,
        totalCount,
        setTotalCount,
        selectedIds,
        toggleSelectedId,
        selectAllIds,
        clearSelection,
        closeDrawer,
      }}
    >
      {children}
    </NotificationFilterContext.Provider>
  )
}

export const useNotificationFilter = () => {
  const context = useContext(NotificationFilterContext)
  if (context === undefined) {
    throw new Error(
      'useNotificationFilter must be used within a NotificationFilterProvider'
    )
  }
  return context
}
